import React, { useEffect, useState } from "react";
import * as R from "ramda";
import _ from "lodash";
import moment from "moment-timezone";

import {
  LineChart,
  MachineConsumptionForm,
  SearchableSelect,
} from "../../../components";
import { LineChartProps } from "../../../utils/types/components";
import { getMachineFeStatus, getMachineFeInfo } from "../../../services/models";
import { getMachineFeInfoPropsResponseData } from "../../../utils/types/services";
import { MachineFeProjectionProps } from "../../../utils/types/components";
import { asyncLoopSeq } from "../../../utils/functions";

export const MachineFeProjection = ({
  setLoading,
  setDialog,
}: MachineFeProjectionProps): React.ReactElement => {
  const [chartData, setChartData] = useState<LineChartProps["data"]>([]);
  const [availableModels, setAvailabelModels] = useState<
    getMachineFeInfoPropsResponseData[]
  >([]);

  const [
    modelSelected,
    setModelSelected,
  ] = useState<getMachineFeInfoPropsResponseData>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await getMachineFeInfo();
      setAvailabelModels(res?.data);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!modelSelected) setChartData([]);
  }, [modelSelected]);

  const handleMachineFeStatus = async (formData: Record<string, number>) => {
    const modelId = modelSelected?.model_id;

    if (!modelId) {
      setDialog({
        content: "Selecciona un modelo",
        handleAccept: () => setDialog({ open: false }),
      });
      return;
    }

    setLoading(true);

    const createParams = (days: number) => ({
      params: { ...formData, dayProjections: days },
      modelId,
    });

    const firstInterval = _.round(formData.dayProjections / 3);
    const secondInterval = _.round(firstInterval * 2);
    const thirdInterval = _.round(firstInterval * 3);

    const resData = await asyncLoopSeq(getMachineFeStatus, [
      createParams(firstInterval),
      createParams(secondInterval),
      createParams(thirdInterval),
    ]);

    const round3 = (v: number) => _.round(v, 3);

    let data = resData.reduce(
      (acc, item, index) => [
        ...acc,
        {
          y: round3(item.data.fe),
          x: moment(formData.baseDate, "YYYY-MM-DD")
            .add(firstInterval * (index + 1), "days")
            .format("DD-MM-YYYY"),
        },
      ],
      [],
    );

    data = R.insert(
      0,
      {
        y: round3(formData.Fe),
        x: moment(formData.baseDate, "YYYY-MM-DD").format("DD-MM-YYYY"),
      },
      data,
    );

    setLoading(false);
    setChartData([
      {
        id: "Equipo",
        data,
      },
    ]);
  };

  const getModelLabel = ({ machinary = [] }) =>
    machinary.reduce(
      (acc: string, { level_6, unidad, componente }) =>
        `${acc && acc + " # "} ${level_6} ⟶ ${unidad} ⟶ ${componente}`,
      "",
    );

  const getModelDefaultValues = ({ params = {} }) =>
    R.pipe(
      R.keys,
      R.map((key) => ({
        id: key,
        value: params[key]?.default_value,
      })),
    )(params);

  return (
    <view style={{ width: "100%" }}>
      <SearchableSelect
        label="Modelo Predictivo"
        options={availableModels}
        getOptionLabel={(opt) => getModelLabel(opt)}
        onChange={(y) =>
          setModelSelected(y as getMachineFeInfoPropsResponseData)
        }
      />

      {modelSelected && (
        <MachineConsumptionForm
          onSubmit={handleMachineFeStatus}
          defaultValues={getModelDefaultValues(modelSelected)}
        />
      )}

      {!_.isEmpty(chartData) && (
        <div style={{ width: "100%", height: 500 }}>
          <LineChart
            data={chartData}
            curve="natural"
            axisLeftLegend="Concentración Fe"
            axisBottom={{
              format: "%b %d",
              legend: "Tiempo",
              legendOffset: 36,
              legendPosition: "middle",
            }}
            xFormat="time:%d-%m-%Y"
            xScale={{
              type: "time",
              format: "%d-%m-%Y",
              useUTC: false,
              precision: "day",
            }}
          />
        </div>
      )}
    </view>
  );
};
